import { JsonObject } from '../../app/json/jsonValue.js';
import { GetParentJsonValueHandler } from './getParentJsonValueHandler.js';
import type { ITransientStore } from './iTransientStore.js';

/**
 * Abstract base class for a server-side CLIENT-DATA preparer.
 *
 * Produces the RAW (unfiltered) data a browser-side client handler will later filter/render WITHOUT a
 * server round-trip. Distinct from {@link IData}: an IData produces the FINAL data baked into the
 * component during the server merge; an IClientData produces the RAW set that the server either embeds
 * into the component's HTML (DataSource=Embedded) or exposes via the handler-data endpoint
 * (DataSource=Remote), so the browser can process it client-side.
 *
 * The context parameters are NORMALIZED to match {@link IData}.getData (the same 12 parameters, in the same
 * order) so the preparer resolves values identically to the server data handler; the SAME producer is
 * callable from the server merge step (applyDynamicClientData) and the on-demand handler-data path.
 * paramJson is the handler's static param file (from the binding IParamFile).
 */
export abstract class IClientData {
    /**
     * Gets the full type name for DynamicClientData operation tracking.
     */
    abstract get typeName(): string;

    /**
     * Produces the raw, unfiltered client data for a component in a single server-side pass. Override to
     * read the handler's source and return the full set the browser handler will filter/render. Return an
     * empty JsonObject when the handler does not apply.
     */
    getClientData(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null = null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): JsonObject {
        return new JsonObject();
    }
}
